import { businessTimezone, computeCurrentDateTime, now } from '#lib/stores/time.js';
import { selectedLocale, t } from '#lib/stores/i18n.js';
import { DateTime } from 'luxon';
import { get } from 'svelte/store';

export const MIN_MINUTE_AFTER_MIDNIGHT = 5 * 60;
export const MAX_MINUTE_AFTER_MIDNIGHT = (24 + 4.5) * 60;

export function convertTimeToMinutes(
	time: DateTime | string,
	shift5hours: boolean = false
): number {
	let hours, minutes;
	if (typeof time === 'string') {
		[hours, minutes] = time.split(':').map((x) => parseInt(x));
	} else {
		hours = time.hour;
		minutes = time.minute;
	}
	if (shift5hours && hours < 5) {
		hours = hours + 24;
	}
	return hours * 60 + minutes;
}

export function convertDayToNumber(day: string): number {
	switch (day) {
		case 'sunday':
			return 7;
		case 'monday':
			return 1;
		case 'tuesday':
			return 2;
		case 'wednesday':
			return 3;
		case 'thursday':
			return 4;
		case 'friday':
			return 5;
		case 'saturday':
			return 6;
	}
	return -1;
}

export function formatDuration(duration: number): string {
	let out = '';
	const hours = Math.floor(duration / 60);
	if (hours > 0) {
		out += `${hours}h `;
	}
	const minutes = duration % 60;
	return out + `${minutes}m`;
}

export function minutesFromMidnightToLocalizedTime(minutesFromMidnight: number): string {
	return get(now)
		.startOf('year') // to avoid DST issues
		.startOf('day')
		.plus({ minutes: minutesFromMidnight })
		.toLocaleString(DateTime.TIME_SIMPLE);
}

export function minutesToReadableTime(minutes: number): string {
	const days = Math.floor(minutes / 1440);
	const hours = Math.floor((minutes % 1440) / 60);
	const mins = minutes % 60;
	let out = '';
	if (days > 0) {
		out += get(t)('days-short', { d: days.toString() }) + ' ';
	}
	if (hours > 0) {
		out += get(t)('hours-short', { h: hours.toString() }) + ' ';
	}
	if (mins > 0) {
		out += get(t)('min-short', { min: mins.toString() });
	}
	if (minutes === 0) {
		out += get(t)('min-short', { min: '0' });
	}
	return out;
}

export function extractValidityStartAndEnd(validityDates: string[] | null | undefined): {
	validityStart: DateTime | null;
	validityEnd: DateTime | null;
} {
	if (!validityDates || validityDates.length === 0) {
		return { validityStart: null, validityEnd: null };
	}

	let minStartDate: string | undefined | null = undefined;
	let maxEndDate: string | undefined | null = undefined;

	validityDates.forEach((date) => {
		const [dateStart, dateEnd] = date.split('/');
		if (dateStart === 'null') minStartDate = null;
		if (dateEnd === 'null') maxEndDate = null;
		if (minStartDate !== null && (minStartDate === undefined || dateStart < minStartDate)) {
			minStartDate = dateStart;
		}
		if (maxEndDate !== null && (maxEndDate === undefined || dateEnd > maxEndDate)) {
			maxEndDate = dateEnd;
		}
	});

	return {
		validityStart: DateTimefromYmd(minStartDate),
		validityEnd: DateTimefromYmd(maxEndDate)
	};
}

export function isDateInValidityDates(
	validityDates: string[] | null | undefined,
	date: DateTime | string | null | undefined
): boolean {
	if (!validityDates || validityDates.length === 0) {
		return true;
	}

	if (!date) return false;
	const dateStr = typeof date === 'string' ? date : date.toISODate();
	if (!dateStr) return false;

	for (const period of validityDates) {
		const [startStr, endStr] = String(period).split('/');
		const startOk = startStr === 'null' || dateStr >= startStr;
		const endOk = endStr === 'null' || dateStr <= endStr;

		if (startOk && endOk) {
			return true;
		}
	}

	return false;
}

export function formatRemainingRelative(
	targetDate: DateTime | null | undefined,
	originDate?: DateTime
): string | null {
	if (!targetDate) return null;
	const currentNow = originDate ?? get(now);
	if (targetDate <= currentNow) {
		return get(t)('giftcard.expired');
	}
	const nowTime = currentNow.startOf('day');
	const target = targetDate.startOf('day');
	const totalDays = Math.ceil(target.diff(nowTime, 'days').days);

	if (totalDays <= 0) {
		return get(t)('giftcard.expired');
	}
	if (totalDays === 1) {
		return get(t)('time.unit.day');
	}

	const diff = target.diff(nowTime, ['years', 'months', 'days']).toObject();
	const years = Math.floor(diff.years || 0);
	const months = Math.floor(diff.months || 0);
	const days = Math.floor(diff.days || 0);

	const parts: string[] = [];
	if (years > 0) {
		parts.push(
			years === 1
				? get(t)('time.unit.year')
				: get(t)('time.unit.years', { count: years.toString() })
		);
	}
	if (months > 0) {
		parts.push(
			months === 1
				? get(t)('time.unit.month')
				: get(t)('time.unit.months', { count: months.toString() })
		);
	}
	if (days > 0 && years === 0) {
		parts.push(
			days === 1
				? get(t)('time.unit.day')
				: get(t)('time.unit.days', { count: days.toString() })
		);
	}

	let joined = '';
	if (parts.length === 1) {
		joined = parts[0];
	} else if (parts.length >= 2) {
		joined = `${parts[0]} ${get(t)('time.and')} ${parts[1]}`;
	} else {
		joined = get(t)('time.unit.days', { count: totalDays.toString() });
	}

	return joined;
}

export function DateTimefromISO(date: string | DateTime | null | undefined): DateTime | null {
	if (!date) return null;
	if (typeof date !== 'string') return date;
	let dt = DateTime.fromISO(date);
	if (!dt.isValid) {
		dt = DateTime.fromSQL(date);
	}
	return dt.isValid ? dt.setZone(get(businessTimezone)) : null;
}

export function DateTimefromYmd(date: string | DateTime | null | undefined): DateTime | null {
	if (!date) return null;
	if (typeof date !== 'string') return date;
	return DateTime.fromISO(date, { zone: get(businessTimezone) })!;
}

type FormatDateOptions =
	'ISO' | 'withYear' | 'withSlash' | 'monthText' | Intl.DateTimeFormatOptions | null;

// NOTE: Strictly requires a hydrated Luxon DateTime (or null/undefined).
// DO NOT accept strings here: fail-fast ensures unhydrated models or missing conversions are caught immediately.
export function formatDate(
	date: DateTime | null | undefined,
	options: FormatDateOptions = null,
	params: {
		addYearIfDifferent?: boolean;
		useShortNames?: boolean;
		timezone?: string;
	} = {}
): string {
	if (!date) return '';

	const timezone = params.timezone ?? get(businessTimezone);
	const addYearIfDifferent = params.addYearIfDifferent ?? true;
	const useShortNames = params.useShortNames ?? true;

	if (timezone.toLowerCase() === 'utc') {
		console.error('Timezone is not set for formatDate');
	}

	const targetDate = date;

	// Today of the businessTimezone (respects time travel simulation)
	const currentNow = computeCurrentDateTime().setZone(timezone);

	let dateOptions: Intl.DateTimeFormatOptions = {};
	if (!options) {
		dateOptions = { weekday: 'short', day: 'numeric', month: 'short' };
	} else if (options === 'ISO') {
		return targetDate.toISODate() ?? 'null';
	} else if (options === 'withYear') {
		dateOptions = { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' };
	} else if (options === 'monthText') {
		dateOptions = { day: 'numeric', month: 'short', year: 'numeric' };
	} else if (options === 'withSlash') {
		dateOptions = { day: 'numeric', month: 'numeric', year: 'numeric' };
	} else {
		dateOptions = { ...options };
	}

	if (
		targetDate.year !== currentNow.year &&
		addYearIfDifferent &&
		!('dateStyle' in dateOptions) &&
		!('timeStyle' in dateOptions)
	) {
		dateOptions.year = 'numeric';
	}

	if (useShortNames) {
		if (currentNow.toISODate() === targetDate.toISODate()) {
			return get(t)('Today');
		} else if (currentNow.toISODate() === targetDate.plus({ days: 1 }).toISODate()) {
			return get(t)('Yesterday');
		} else if (currentNow.toISODate() === targetDate.minus({ days: 1 }).toISODate()) {
			return get(t)('Tomorrow');
		}
	}

	return targetDate.toLocaleString(dateOptions, { locale: get(selectedLocale) });
}

type FormatDateTimeOptions = {
	timeStyle?: 'short' | 'medium' | 'long' | 'full';
};

// NOTE: Strictly requires a hydrated Luxon DateTime (or null/undefined).
// DO NOT accept strings here: fail-fast ensures unhydrated models or missing conversions are caught immediately.
export function formatTime(
	date: DateTime | null | undefined,
	options: FormatDateTimeOptions = {},
	params: { timezone?: string } = {}
): string {
	if (!date) return '';

	const timezone = params.timezone ?? get(businessTimezone);

	if (timezone.toLowerCase() === 'utc') {
		console.error('Timezone is not set for formatTime');
	}

	return date
		.setZone(timezone)
		.toLocaleString(
			{ timeStyle: options.timeStyle ?? 'short' },
			{ locale: get(selectedLocale) }
		);
}
